<template>
    <div class="fin-panel">
        <skeleton-loader v-if="showSkeleton" type="stats-panel" />

        <template v-else>
            <div class="fin-kpis">
                <kpi-card :label="$t('business.incomes')" :value="formatMoney(totalIncomes)"
                    :color="$vuetify.theme.currentTheme.success" />
                <kpi-card :label="$t('business.expenses')" :value="formatMoney(totalExpenses)"
                    :color="$vuetify.theme.currentTheme.error" />
                <kpi-card :label="$t('business.result')" :value="formatMoney(result)" />
                <kpi-card :label="$t('business.margin')" :value="marginLabel" />
            </div>

            <div class="fin-donuts">
                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.incomes') }}</span>
                    </template>
                    <donut-breakdown :items="incomeItems" :size="120" :center-value="formatMoney(totalIncomes)"
                        :center-label="$t('business.total')" :center-color="$vuetify.theme.currentTheme.success" />
                </stat-card>

                <stat-card>
                    <template v-slot:header>
                        <span class="chart-title">{{ $t('business.expenses') }}</span>
                    </template>
                    <donut-breakdown :items="expenseItems" :size="120" :center-value="formatMoney(totalExpenses)"
                        :center-label="$t('business.total')" :center-color="$vuetify.theme.currentTheme.error" />
                </stat-card>
            </div>
        </template>

        <stat-card>
            <template v-slot:header>
                <span class="chart-title">{{ $t('business.yearly_evolution') }}</span>
                <span class="chart-year">{{ year }}</span>
            </template>
            <skeleton-loader v-if="loadingYear" type="bar-chart" />
            <div v-else-if="!hasYearData" class="fin-empty">{{ $t('business.no_month_data') }}</div>
            <div v-else class="fin-chart">
                <grouped-bar-chart :labels="monthLabels" :datasets="yearDatasets" :value-formatter="formatMoney"
                    :height="220" />
            </div>
        </stat-card>
    </div>
</template>

<script>
import KpiCard from "@/components/common/KpiCard";
import StatCard from "@/components/statistics/StatCard";
import DonutBreakdown from "@/components/common/DonutBreakdown";
import GroupedBarChart from "@/components/common/GroupedBarChart";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import {
    EXPENSE_COLORS, INCOME_COLORS, formatMoney, loadCashYear, sumArea, sumExpenses, sumIncomes,
} from "@/utils/Business";

export default {
    name: "StatsFinancial",
    components: { KpiCard, StatCard, DonutBreakdown, GroupedBarChart, SkeletonLoader },
    props: {
        companyId: {
            type: String,
            default: null,
        },
        period: {
            type: String,
            default: '',
        },
        cash: {
            type: Object,
            default: null,
        },
        clientsIncome: {
            type: Number,
            default: 0,
        },
        clientsIncomeByPeriod: {
            type: Object,
            default: () => ({}),
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loadingYear: true,
            loadedOnce: false,
            year: null,
            months: {},
        }
    },
    computed: {
        showSkeleton(vm = this) {
            return vm.loading && !vm.loadedOnce
        },
        totalExpenses(vm = this) {
            return sumExpenses(vm.cash)
        },
        totalIncomes(vm = this) {
            return sumIncomes(vm.cash, vm.clientsIncome)
        },
        result(vm = this) {
            return vm.totalIncomes - vm.totalExpenses
        },
        marginLabel(vm = this) {
            if (!vm.totalIncomes) return vm.$t('common.without_data')
            return `${Math.round(vm.result / vm.totalIncomes * 100)}%`
        },
        incomeItems(vm = this) {
            const manual = (vm.cash?.incomes || [])
                .filter(item => Number(item.amount) > 0)
                .map(item => ({ label: item.name, value: Number(item.amount) }))

            const items = vm.clientsIncome > 0
                ? [{ label: vm.$t('business.clients_income'), value: vm.clientsIncome }, ...manual]
                : manual

            return items.map((item, i) => ({ ...item, color: INCOME_COLORS[i % INCOME_COLORS.length] }))
        },
        expenseItems(vm = this) {
            return (vm.cash?.expenses || [])
                .map(area => ({ label: area.name, value: sumArea(area) }))
                .filter(item => item.value > 0)
                .map((item, i) => ({ ...item, color: EXPENSE_COLORS[i % EXPENSE_COLORS.length] }))
        },
        monthLabels(vm = this) {
            void vm.$i18n.locale
            return Array.from({ length: 12 }, (item, i) => {
                return vm.$moment().month(i).format('MMM').replace('.', '').toUpperCase()
            })
        },
        yearSeries(vm = this) {
            const incomes = []
            const expenses = []
            for (let month = 1; month <= 12; month += 1) {
                const key = `${vm.year}-${String(month).padStart(2, '0')}`
                const monthCash = key === vm.period ? vm.cash : vm.months[key]
                const autoIncome = vm.clientsIncomeByPeriod[key] || 0
                const hasData = !!monthCash || autoIncome > 0
                incomes.push(hasData ? sumIncomes(monthCash, autoIncome) : null)
                expenses.push(monthCash ? sumExpenses(monthCash) : null)
            }
            return { incomes, expenses }
        },
        hasYearData(vm = this) {
            return vm.yearSeries.incomes.some(value => value !== null)
                || vm.yearSeries.expenses.some(value => value !== null)
        },
        yearDatasets(vm = this) {
            const highlight = (color) => Array.from({ length: 12 }, (_, index) => {
                const key = `${vm.year}-${String(index + 1).padStart(2, '0')}`
                return key === vm.period ? color : `${color}66`
            })
            return [
                {
                    label: vm.$t('business.incomes'),
                    data: vm.yearSeries.incomes,
                    backgroundColor: highlight(INCOME_COLORS[0]),
                },
                {
                    label: vm.$t('business.expenses'),
                    data: vm.yearSeries.expenses,
                    backgroundColor: highlight(EXPENSE_COLORS[0]),
                },
            ]
        },
    },
    mounted() {
        this.loadYear()
    },
    methods: {
        formatMoney,
        async loadYear() {
            const vm = this
            const year = Number(String(vm.period).slice(0, 4))
            if (!vm.companyId || !year) return
            vm.loadingYear = true
            vm.year = year
            try {
                vm.months = await loadCashYear(vm.companyId, year)
            } catch (error) {
                console.error('[business] loadCashYear', error?.code, error?.message)
                vm.months = {}
            } finally {
                vm.loadingYear = false
            }
        },
    },
    watch: {
        loading(value) {
            if (!value) this.loadedOnce = true
        },
        period(value, previous) {
            if (String(value).slice(0, 4) !== String(previous).slice(0, 4)) this.loadYear()
        },
    },
}
</script>

<style scoped>
.fin-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.fin-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
}

.fin-donuts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.chart-title {
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.chart-year {
    margin-left: auto;
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.fin-chart {
    height: 220px;
}

.fin-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 960px) {
    .fin-kpis {
        grid-template-columns: repeat(2, 1fr);
    }

    .fin-donuts {
        grid-template-columns: 1fr;
    }
}
</style>
